import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { MessageSquare, Download, RefreshCw, AlertCircle, TrendingUp, Heart, BarChart3, FileJson, FileSpreadsheet, CreditCard, ExternalLink } from 'lucide-react';
import { exportCommentsCSV, exportCommentsJSON, getCreditBalance } from '../../lib/api';
import { useEnhancedComments } from '../../hooks/useEnhancedComments';
import { CommentsPreview } from './CommentsPreview';
import { CreditWarningModal } from './CreditWarningModal';
import { CommentsProgress } from './CommentsProgress';
interface CommentAnalysisProps {
  taskId: string;
  videoId?: string;
}
export function CommentAnalysis({
  taskId,
  videoId
}: CommentAnalysisProps) {
  const {
    preview,
    previewLoading,
    previewError,
    fetching,
    progress,
    results,
    showCreditModal,
    setShowCreditModal,
    alreadyFetched,
    insufficientCredits,
    creditsError,
    initiateFetch
  } = useEnhancedComments(taskId);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);
  // Load current credit balance when insufficient credits error occurs
  useEffect(() => {
    if (insufficientCredits) {
      loadCredits();
    }
  }, [insufficientCredits]);
  const loadCredits = async () => {
    try {
      const balance = await getCreditBalance();
      setCurrentCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };
  const handleFetchClick = () => {
    setShowCreditModal(true);
  };
  const handleConfirmFetch = () => {
    initiateFetch();
  };
  const handleExportCSV = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportCommentsCSV(taskId);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setExportError('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  const handleExportJSON = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportCommentsJSON(taskId);
    } catch (err) {
      console.error('Error exporting JSON:', err);
      setExportError('Failed to export JSON. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  // Show insufficient credits error
  if (insufficientCredits && creditsError) {
    return <div className="border border-destructive/30 rounded-lg bg-destructive/5" data-id="element-895">
        <div className="p-4" data-id="element-896">
          <div className="flex items-start gap-3 mb-4" data-id="element-897">
            <AlertCircle size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-898" />
            <div className="flex-grow" data-id="element-899">
              <h4 className="font-medium text-sm text-destructive mb-1" data-id="element-900">
                Insufficient Credits
              </h4>
              <p className="text-sm text-muted-foreground mb-3" data-id="element-901">
                {creditsError}
              </p>
              {preview && <div className="bg-secondary/30 rounded-lg p-3 mb-3" data-id="element-902">
                  <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-903">
                    <div data-id="element-904">
                      <span className="text-muted-foreground" data-id="element-905">
                        Estimated comments:
                      </span>
                      <span className="font-medium ml-2" data-id="element-906">
                        {preview.estimated_total.toLocaleString()}
                      </span>
                    </div>
                    <div data-id="element-907">
                      <span className="text-muted-foreground" data-id="element-908">
                        Credits needed:
                      </span>
                      <span className="font-medium ml-2" data-id="element-909">
                        {preview.credits_needed}
                      </span>
                    </div>
                    {currentCredits !== null && <>
                        <div data-id="element-910">
                          <span className="text-muted-foreground" data-id="element-911">
                            Your balance:
                          </span>
                          <span className="font-medium ml-2" data-id="element-912">
                            {currentCredits} credits
                          </span>
                        </div>
                        <div data-id="element-913">
                          <span className="text-muted-foreground" data-id="element-914">Need:</span>
                          <span className="font-medium ml-2 text-destructive" data-id="element-915">
                            {Math.max(0, preview.credits_needed - currentCredits)}{' '}
                            more
                          </span>
                        </div>
                      </>}
                  </div>
                </div>}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2" data-id="element-916">
            <Button variant="default" size="sm" className="flex-1" onClick={() => window.open('/app/settings', '_blank')} data-id="element-917">
              <CreditCard size={14} className="mr-2" data-id="element-918" />
              Buy Credits
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => window.location.reload()} data-id="element-919">
              <RefreshCw size={14} className="mr-2" data-id="element-920" />
              Refresh
            </Button>
          </div>

          <div className="mt-3 pt-3 border-t border-border/30" data-id="element-921">
            <p className="text-xs text-muted-foreground" data-id="element-922">
              💡 <strong data-id="element-923">How it works:</strong> 1 credit = 100 comments. Credits
              are only charged after successful analysis.
            </p>
          </div>
        </div>
      </div>;
  }
  // Show progress if fetching
  if (fetching && progress) {
    return <CommentsProgress progress={progress} data-id="element-924" />;
  }
  // Show results if completed
  if (results && !fetching) {
    return <div className="border border-primary/30 rounded-lg bg-card" data-id="element-925">
        <div className="p-4 border-b border-border/30" data-id="element-926">
          <div className="flex items-center justify-between mb-3" data-id="element-927">
            <div className="flex items-center gap-2" data-id="element-928">
              <MessageSquare size={16} className="text-primary" data-id="element-929" />
              <h4 className="font-medium text-sm" data-id="element-930">Comment Analysis Results</h4>
            </div>
            <div className="flex items-center gap-2" data-id="element-931">
              <Button variant="ghost" size="sm" onClick={handleExportCSV} disabled={isExporting} className="h-8 px-2" data-id="element-932">
                <FileSpreadsheet size={14} className="mr-1" data-id="element-933" />
                CSV
              </Button>
              <Button variant="ghost" size="sm" onClick={handleExportJSON} disabled={isExporting} className="h-8 px-2" data-id="element-934">
                <FileJson size={14} className="mr-1" data-id="element-935" />
                JSON
              </Button>
            </div>
          </div>

          {exportError && <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive flex items-center gap-2" data-id="element-936">
              <AlertCircle size={14} data-id="element-937" />
              {exportError}
            </div>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-938">
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-939">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-940">
                <MessageSquare size={12} data-id="element-941" />
                Total Comments
              </div>
              <div className="text-lg font-semibold" data-id="element-942">
                {results.total_comments}
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-943">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-944">
                <Heart size={12} data-id="element-945" />
                Avg Likes
              </div>
              <div className="text-lg font-semibold" data-id="element-946">
                {Math.round(results.avg_likes).toLocaleString()}
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-947">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-948">
                <TrendingUp size={12} data-id="element-949" />
                Engagement
              </div>
              <div className="text-lg font-semibold" data-id="element-950">
                {results.engagement_rate.toFixed(1)}%
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-951">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-952">
                <BarChart3 size={12} data-id="element-953" />
                Sentiment
              </div>
              <div className="text-sm font-semibold capitalize" data-id="element-954">
                {results.sentiment.dominant} ({results.sentiment.positive}%)
              </div>
            </div>
          </div>
        </div>

        <div className="p-4" data-id="element-955">
          <h5 className="text-sm font-medium mb-3 flex items-center gap-2" data-id="element-956">
            <TrendingUp size={14} className="text-primary" data-id="element-957" />
            Top Comments
          </h5>

          <div className="overflow-x-auto" data-id="element-958">
            <table className="w-full text-sm" data-id="element-959">
              <thead className="border-b border-border/30" data-id="element-960">
                <tr className="text-left" data-id="element-961">
                  <th className="pb-2 pr-4 font-medium text-muted-foreground" data-id="element-962">
                    Author
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground" data-id="element-963">
                    Comment
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground text-right" data-id="element-964">
                    Likes
                  </th>
                  <th className="pb-2 font-medium text-muted-foreground text-right" data-id="element-965">
                    Replies
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-966">
                {results.top_comments.map((comment, index) => <tr key={index} className="border-b border-border/20 last:border-0" data-id="element-967">
                    <td className="py-3 pr-4 text-xs" data-id="element-968">{comment.author}</td>
                    <td className="py-3 pr-4 text-xs max-w-xs truncate" data-id="element-969">
                      {comment.comment || comment.text}
                    </td>
                    <td className="py-3 pr-4 text-xs text-right" data-id="element-970">
                      {comment.likes.toLocaleString()}
                    </td>
                    <td className="py-3 text-xs text-right" data-id="element-971">
                      {comment.replies}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="mt-4 pt-4 border-t border-border/20 flex items-center justify-between" data-id="element-972">
            <p className="text-xs text-muted-foreground" data-id="element-973">
              Showing {results.top_comments.length} of {results.total_comments}{' '}
              comments
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} data-id="element-974">
              <RefreshCw size={14} className="mr-1" data-id="element-975" />
              Refresh
            </Button>
          </div>
        </div>
      </div>;
  }
  // Show preview by default
  return <>
      <CommentsPreview preview={preview} loading={previewLoading} error={previewError} alreadyFetched={alreadyFetched} onFetchClick={handleFetchClick} data-id="element-976" />

      <CreditWarningModal isOpen={showCreditModal} onClose={() => setShowCreditModal(false)} onConfirm={handleConfirmFetch} creditsNeeded={preview?.credits_needed || 0} estimatedTotal={preview?.estimated_total || 0} data-id="element-977" />
    </>;
}